import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, Clock, Users, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CITY_META = {
  london: { name: "London", country: "United Kingdom", blurb: "SIA-licensed guards only. Members clubs, arenas, and retail — paid in pounds, within the hour." },
  manchester: { name: "Manchester", country: "United Kingdom", blurb: "Warehouse raves, arena nights, and matchday control. SIA-licensed crews, paid in pounds within the hour." },
  birmingham: { name: "Birmingham", country: "United Kingdom", blurb: "From the NEC to Digbeth's warehouse scene — big-footprint events need verified guards at scale." },
  glasgow: { name: "Glasgow", country: "United Kingdom", blurb: "Barrowland to the Hydro: legendary venues, serious door standards, zero tolerance for no-shows." },
  leeds: { name: "Leeds", country: "United Kingdom", blurb: "Arena nights, Call Lane doors, and matchday villages — SIA crews paid within the hour." },
  bristol: { name: "Bristol", country: "United Kingdom", blurb: "From Motion's warehouse to Harbourside festivals — the South West's busiest door scene." },
  edinburgh: { name: "Edinburgh", country: "United Kingdom", blurb: "Fringe crowds, Murrayfield scale, and Old Town doors. Festival-hardened guards wanted." },
};

function fmtDate(d) {
  return new Date(`${d}T00:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

const typeStyles = {
  Armed: "bg-red-500/10 text-red-400",
  Supervisor: "bg-amber-500/10 text-amber-500",
  Unarmed: "bg-emerald-500/10 text-emerald-400",
  Event: "bg-sky-500/10 text-sky-400",
};

export default function CityPage() {
  const { city } = useParams();
  const navigate = useNavigate();
  const [shifts, setShifts] = useState(null);
  const meta = CITY_META[(city || "").toLowerCase()];

  useEffect(() => {
    fetch(`${API}/shifts`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setShifts((d?.shifts || []).filter((s) => s.city.toLowerCase() === city?.toLowerCase())))
      .catch(() => setShifts([]));
  }, [city]);

  if (!meta) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[#07090e] px-4 text-center">
        <p className="font-display text-3xl font-black uppercase text-slate-50">Unknown city</p>
        <p className="text-sm text-slate-500">GuardGig28 launches in London, Manchester, Birmingham, Glasgow, Leeds, Bristol, and Edinburgh first.</p>
        <Link to="/" data-testid="city-unknown-back" className="font-mono-tag text-[10px] text-amber-500 underline underline-offset-4">Back to GuardGig28</Link>
      </div>
    );
  }

  const sym = (s) => (s?.currency === "GBP" ? "£" : "$");
  const totalSpots = (shifts || []).reduce((a, s) => a + (s.spots || 0), 0);
  const maxRate = shifts?.length ? Math.max(...shifts.map((s) => s.rate)) : 0;
  const topShift = shifts?.find((s) => s.rate === maxRate);

  return (
    <div data-testid="city-page" className="min-h-screen bg-[#07090e] pb-24">
      <header className="border-b border-white/5 bg-[#07090e]/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6">
          <button data-testid="city-back-btn" onClick={() => navigate("/")} className="flex items-center gap-2 text-slate-400 transition-colors hover:text-amber-500">
            <ArrowLeft className="h-4 w-4" />
            <span className="font-mono-tag text-[10px]">All cities</span>
          </button>
          <span className="font-display text-lg font-extrabold uppercase text-slate-50">Guard<span className="text-amber-500">Gig</span>28</span>
          <span className="font-mono-tag text-[9px] text-emerald-400">Live Sep 28</span>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 pt-16 sm:px-6">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <p className="font-mono-tag text-[11px] text-amber-500">{meta.country} · Launch city</p>
          <h1 data-testid="city-title" className="font-display mt-4 text-5xl font-black uppercase leading-[0.95] tracking-tight sm:text-7xl">
            {meta.name}
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-400">{meta.blurb}</p>
        </motion.div>

        {shifts === null ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-amber-500" /></div>
        ) : (
          <>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="mt-12 grid grid-cols-3 gap-4 max-w-2xl"
            >
              {[
                [shifts.length, "shifts posted"],
                [totalSpots, "open spots"],
                [`${sym(topShift)}${maxRate}/hr`, "top rate"],
              ].map(([v, l]) => (
                <div key={l} className="rounded-lg border border-white/8 bg-[#0f141c] px-5 py-5 text-center">
                  <p data-testid={`city-stat-${l.replace(/\s+/g, "-")}`} className="font-display text-3xl font-black text-slate-50">{v}</p>
                  <p className="font-mono-tag mt-1 text-[8px] text-slate-500">{l}</p>
                </div>
              ))}
            </motion.div>

            <div className="mt-10 overflow-hidden rounded-xl border border-white/8 bg-[#0f141c]">
              {shifts.length === 0 ? (
                <p className="py-16 text-center font-mono-tag text-[10px] text-slate-600">Opening shifts dropping soon</p>
              ) : shifts.map((s, i) => (
                <motion.div
                  key={s.id}
                  data-testid={`city-shift-${i}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.45, delay: 0.1 + i * 0.05 }}
                  className="flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-white/5 px-5 py-4 transition-colors last:border-b-0 hover:bg-white/[0.02] sm:px-7"
                >
                  <div className="min-w-[180px] flex-1">
                    <p className="font-display text-lg font-bold uppercase leading-tight text-slate-50">{s.venue}</p>
                    <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-500"><MapPin className="h-3 w-3" /> {s.area}</p>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <Clock className="h-3.5 w-3.5 text-slate-600" /> {fmtDate(s.date)} · {s.start}–{s.end}
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-slate-500">
                    <Users className="h-3.5 w-3.5" /> {s.spots} spot{s.spots === 1 ? "" : "s"}
                  </div>
                  <span className={`rounded-full px-2.5 py-1 font-mono-tag text-[8px] ${typeStyles[s.type] || "bg-white/5 text-slate-400"}`}>{s.type}</span>
                  <span className="font-display text-xl font-black text-amber-500">{sym(s)}{s.rate}/hr</span>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-10 flex flex-wrap items-center gap-5"
            >
              <Link
                to="/"
                data-testid="city-join-btn"
                className="rounded-md bg-amber-500 px-7 py-3.5 font-display text-sm font-bold uppercase text-[#07090e] transition-all hover:bg-amber-400 hover:shadow-[0_0_24px_rgba(245,158,11,0.35)]"
              >
                Join the launch list
              </Link>
              <p className="font-mono-tag text-[9px] text-slate-600">Share this page: /cities/{city.toLowerCase()}</p>
            </motion.div>
          </>
        )}
      </main>
    </div>
  );
}
